import React, { useContext, useState, useEffect, createContext } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';

import { useFetchCampaignSubmissions } from '..';
import { getCurrentPage } from '@vp-ui/widgets/VpMediaTable/helpers';

export const MediaTableContext = createContext();

const MediaTableContextProviderComponent = ({ children, match, location, history }) => {
  const initialSubmissionId = match?.params?.submissionId;
  // this wasn't the best naming decision, but selectedEntity refers to
  // the entity being deleted (either a response or submission)
  // selectedEntity != null => delete modal shows
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  /**
   * { media/attachmentId: { type: mediaType, id: media/attachmentId } }
   */
  const [selectedMediaMap, setSelectedMediaMap] = useState(new Map());
  /**
   * page1: {
   *  submissionId: {
   *    media/attachmentId: { type: mediaType, id: media/attachmentId }
   *  }
   * }
   */
  const [mediaMap, setMediaMap] = useState({});
  // Pending values can be seeded from location.state to survive route remounts
  const [pendingTab, setPendingTab] = useState(location?.state?.pendingTab ?? null);
  const [pendingExpandVersions, setPendingExpandVersions] = useState(location?.state?.pendingExpandVersions ?? null);

  // which submission is being viewed on the details modal
  const [selectedSubmissionId, setSelectedSubmissionId] = useState(initialSubmissionId || null);

  // Clear location state after reading it so back-navigation doesn't re-apply it
  useEffect(() => {
    if (location?.state?.pendingTab != null || location?.state?.pendingExpandVersions != null) {
      history.replace({ ...location, state: {} });
    }
  }, []);

  // setting which media of a submission is being viewed in the details modal
  const [selectedMediaId, setSelectedMediaId] = useState(null);
  const [updatingThumbnail, setUpdatingThumbnail] = useState(false);

  const { data } = useFetchCampaignSubmissions();
  const submissionList = data?.data || [];

  useEffect(() => {
    const tempPage = getCurrentPage(data?.meta);
    setCurrentPage(tempPage);

    const tempMap = {};
    submissionList
      .filter((submission) => submission?.enabled)
      .forEach((submission) => {
        const tempMediaMap = {};
        const mediaList = [...submission.videoContent, ...submission.attachmentsContent];
        mediaList.forEach((media) => {
          const mediaId = media?.mediaId || media?.attachmentId;
          tempMediaMap[mediaId] = {
            type: media?.type,
            id: mediaId,
          };
        });
        tempMap[submission?.campaignSubmissionId] = tempMediaMap;
      });

    setMediaMap((prevValue) => ({
      ...prevValue,
      [tempPage]: tempMap,
    }));
  }, [submissionList]);

  const getSubmissionIdByMediaId = (mediaId) => {
    for (const page of Object.values(mediaMap)) {
      for (const [subId, mediaObj] of Object.entries(page)) {
        if (mediaObj[mediaId]) return subId;
      }
    }
    return null;
  };

  return (
    <MediaTableContext.Provider
      value={{
        mediaMap,
        selectedEntity,
        setSelectedEntity,
        selectedMediaMap,
        setSelectedMediaMap,
        currentPage,
        selectedSubmissionId,
        setSelectedSubmissionId,
        selectedMediaId,
        setSelectedMediaId,
        updatingThumbnail,
        setUpdatingThumbnail,
        pendingTab,
        setPendingTab,
        pendingExpandVersions,
        setPendingExpandVersions,
        getSubmissionIdByMediaId,
      }}
    >
      {children}
    </MediaTableContext.Provider>
  );
};

MediaTableContextProviderComponent.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
  match: PropTypes.object,
  location: PropTypes.object,
  history: PropTypes.object,
};

export const MediaTableContextProvider = withRouter(MediaTableContextProviderComponent);

export const useMediaTableContext = () => {
  const {
    currentPage,
    mediaMap,
    selectedMediaMap,
    setSelectedMediaMap,
    selectedEntity,
    setSelectedEntity,
    selectedSubmissionId,
    setSelectedSubmissionId,
    selectedMediaId,
    setSelectedMediaId,
    updatingThumbnail,
    setUpdatingThumbnail,
    pendingTab,
    setPendingTab,
    pendingExpandVersions,
    setPendingExpandVersions,
    getSubmissionIdByMediaId,
  } = useContext(MediaTableContext);

  // START HELPER FUNCTIONS
  /**
   *
   * @param {array<string>} mediaIds list of mediaIds
   * @returns {boolean} if some mediaIds are selected and some are not
   */
  const getIsPartialSelected = (mediaIds) => {
    let hasUnselected = false;
    let hasSelected = false;
    for (let i = 0; i < mediaIds?.length; i++) {
      if (selectedMediaMap.has(mediaIds[i])) {
        hasSelected = true;
      } else {
        hasUnselected = true;
      }

      if (hasUnselected && hasSelected) {
        return true;
      }
    }

    return false;
  };
  /**
   *
   * @param {string} submissionId submissionId
   * @returns {array<{ id: string, type: string}>} list of medias for a submission
   */
  const getSubmissionMedia = (submissionId) => {
    const obj = mediaMap?.[currentPage]?.[submissionId] || {};

    return Object.values(obj);
  };
  /**
   *
   * @param {string} submissionId submissionId
   * @returns {array<string>} list of mediaIds for a submission
   */
  const getSubmissionMediaIds = (submissionId) => {
    const obj = mediaMap?.[currentPage]?.[submissionId] || {};

    return Object.keys(obj);
  };
  /**
   *
   * @returns {array<{ id: string, type: string}>} list of medias for page
   */
  const getPageMedia = () => {
    const obj = mediaMap?.[currentPage] || {};
    const submissionIds = Object.keys(obj);

    return submissionIds.map((submission) => getSubmissionMedia(submission))?.flat();
  };
  /**
   *
   * @returns {array<string>} list of mediaIds for a page
   */
  const getPageMediaIds = () => {
    const obj = mediaMap?.[currentPage] || {};
    const submissionIds = Object.keys(obj);

    return submissionIds.map((submission) => getSubmissionMediaIds(submission))?.flat();
  };
  /**
   * adds media to selected map
   * @param {{ id: string, type: string }} media media object
   */
  const addSelectedMedia = (media) => {
    setSelectedMediaMap((prevMap) => new Map([...prevMap, [media?.id, media]]));
  };
  /**
   * removes media from selected map
   * @param {{ id: string, type: string }} media media object
   */
  const removeSelectedMedia = (media) => {
    setSelectedMediaMap((prevMap) => {
      const newState = new Map(prevMap);
      newState.delete(media?.id);

      return newState;
    });
  };
  // END HELPER FUNCTIONS

  // START GET FUNCTIONS
  /**
   * @param {string} mediaId id of the video or attachment
   * @returns {bool} if the media is in the selected map
   */
  const getIsMediaSelected = (mediaId) => selectedMediaMap.has(mediaId);

  /**
   * @param {string} submissionId id of the submission (row)
   * @returns {bool} if all medias of a submission are selected
   */
  const getIsSubmissionAllSelected = (submissionId) => {
    const mediaIds = getSubmissionMediaIds(submissionId);

    return !mediaIds.some((id) => !selectedMediaMap.has(id));
  };

  /**
   * @param {sting} submissionId id of the submission (row)
   * @returns {bool} if some medias of a submission are selected and some are not
   */
  const getIsSubmissionPartialSelected = (submissionId) => {
    const mediaIds = getSubmissionMediaIds(submissionId);

    return getIsPartialSelected(mediaIds);
  };

  /**
   * determines if the checkbox in the table header is selected
   * @returns {bool} if all medias on the page are selected
   */
  const getIsPageAllSelected = () => {
    const mediaIds = getPageMediaIds();
    return !!mediaIds.length && !mediaIds.some((id) => !selectedMediaMap.has(id));
  };

  /**
   * determines if the checkbox in the table header is partial selected
   * @returns {bool} if some medias on the page are selected and some are not
   */
  const getIsPagePartialSelected = () => {
    const mediaIds = getPageMediaIds();

    return getIsPartialSelected(mediaIds);
  };

  /**
   * @returns {array<{ id: string, type: string}>} selected media map values as an array
   */
  const getSelectedMedia = () => [...selectedMediaMap.values()];

  /**
   * @returns {number} number of selected media
   */
  const getSelectedMediaCount = () => selectedMediaMap.size;

  /**
   * @returns {bool} if there is any media selected
   */
  const getIsNoMediaSelected = () => !selectedMediaMap.size;

  /**
   * @returns {array<string>} selected media ids as an array
   */
  const getSelectedVideoIds = () => {
    const selectedMedia = getSelectedMedia();
    return selectedMedia.filter((media) => media.type === 'VIDEO').map((media) => media.id);
  };
  const getSelectedContent = () => {
    const selectedMedia = getSelectedMedia();

    const mapped = selectedMedia
      .filter(
        (media) =>
          media.contentType === 'video' ||
          media.contentType === 'attachment' ||
          media?.type === 'VIDEO' ||
          media?.type === 'IMAGE'
      )
      .map((content) => {
        let contentType = content?.contentType;
        if (!contentType) contentType = content?.type === 'VIDEO' ? 'video' : 'attachment';

        return {
          id: content?.id,
          contentType,
        };
      });

    return mapped;
  };
  /**
   * @returns {array<string>} selected attachment ids as an array
   */
  const getSelectedAttachmentIds = () => {
    const selectedMedia = getSelectedMedia();
    return selectedMedia.filter((media) => media.type !== 'VIDEO').map((media) => media.id);
  };
  /**
   * @returns {array<string>} selected media and attachment ids as an array
   */
  const getSelectedMediaIds = () => [...selectedMediaMap.keys()];
  // END GET FUNCTIONS

  // START UPDATE FUNCTIONS
  /**
   * media checkbox select
   * @param {string} mediaId id of video or attachment
   * @param {bool} selectedOverride manual override to add to selected media map
   */
  const onMediaCheck = (media, selectedOverride) => {
    const id = media?.id || media?.attachmentId || media?.mediaId;
    const newMedia = {
      id,
      type: media?.type,
    };
    !selectedMediaMap.has(id) || selectedOverride
      ? addSelectedMedia(newMedia)
      : removeSelectedMedia(newMedia);
  };

  const onMediaRadioCheck = (media) => {
    setSelectedMediaMap(new Map([[media?.mediaId, media]]));
  };

  /**
   * selects or deselects all media of the submission
   * @param {string} submissionId id of the submission (row)
   */
  const onSubmissionCheck = (submissionId) => {
    const submissionSelected = getIsSubmissionAllSelected(submissionId);
    const selected = !submissionSelected;
    const medias = getSubmissionMedia(submissionId);

    medias.forEach((media) => onMediaCheck(media, selected));
  };

  /**
   * selects or deselects all media on the current page
   */
  const onPageCheck = () => {
    const allSelected = getIsPageAllSelected();
    const selected = !allSelected;
    const medias = getPageMedia();
    medias.forEach((media) => onMediaCheck(media, selected));
  };

  const setSelectedVideos = (videoIds) => {
    const selectedVidsMap = new Map();
    videoIds.forEach((id) => {
      selectedVidsMap.set(id, { type: 'VIDEO', id });
    });

    setSelectedMediaMap(selectedVidsMap);
  };

  const setSelectedContent = (content) => {
    const selectedContentMap = new Map();
    content.forEach((elem) => {
      selectedContentMap.set(elem?.id, { contentType: elem?.contentType, id: elem?.id });
    });

    setSelectedMediaMap(selectedContentMap);
  };
  // END UPDATE FUNCTIONS

  return {
    // checkbox selections
    getIsMediaSelected,
    getIsSubmissionPartialSelected,
    getIsSubmissionAllSelected,
    getIsPagePartialSelected,
    getIsPageAllSelected,

    getSelectedMediaCount,
    getIsNoMediaSelected,
    getSelectedVideoIds,
    getSelectedAttachmentIds,
    getSelectedMediaIds,
    getSelectedContent,
    selectedEntity,
    setSelectedEntity,
    selectedSubmissionId,
    setSelectedSubmissionId,
    selectedMediaId,
    setSelectedMediaId,
    updatingThumbnail,
    setUpdatingThumbnail,

    // update functions
    onMediaCheck,
    onMediaRadioCheck,
    onSubmissionCheck,
    onPageCheck,
    setSelectedVideos,
    setSelectedContent,

    // testing purposes
    getPageMedia,

    // pending state for tab navigation and version expand
    pendingTab,
    setPendingTab,
    pendingExpandVersions,
    setPendingExpandVersions,
    getSubmissionIdByMediaId,
  };
};



// WEBPACK FOOTER //
// ./src/hooks/contexts/useMediaTableContext.js